import { useCallback } from "react";
import { ApiHelper } from "../../api/index";
import { useChatContext } from "../ChatProvider";
import { setLoadingState, updateContext } from "../store/chatSlice";
import { LoadingState } from "../types";

/**
 * Why the client is cancelling. Sent to the backend so logs can tell a
 * deliberate human stop apart from the frontend cancelling on its own —
 * the backend records the declared value verbatim, it never infers.
 */
export type CancelReason = "user_stop" | "client_timeout";

/**
 * Returns a function that cancels the in-flight chat generation:
 * aborts the controller, calls the studio/cancel API, marks the session
 * as CANCELLED, and clears uploaded_files from context.
 */
export const useCancelGeneration = () => {
  const { dispatch, abortController, currentSessionId, sessions } = useChatContext();

  return useCallback(
    async (reason: CancelReason) => {
      if (abortController) {
        abortController.abort();
        dispatch(
          setLoadingState({
            state: LoadingState.CANCELLED,
            sessionId: currentSessionId,
          })
        );

        // Clear uploaded_files when cancelling so subsequent requests start fresh
        if (currentSessionId) {
          const currentContext = sessions[currentSessionId]?.context || {};
          dispatch(
            updateContext({
              sessionId: currentSessionId,
              context: { ...currentContext, uploaded_files: [] },
            })
          );
        }
      }

      if (currentSessionId) {
        try {
          await ApiHelper.post("studio/cancel", {
            session_id: currentSessionId,
            reason,
          });
          dispatch(
            setLoadingState({
              state: LoadingState.CANCELLED,
              sessionId: currentSessionId,
            })
          );
        } catch (error) {
          console.error("Failed to call cancel API:", error);
          // Continue with cancellation even if the API call fails
        }
      }
    },
    [abortController, currentSessionId, sessions, dispatch]
  );
};
